import { Icon } from "client/Components";
import { HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import { HubItemMediaPreviewViewModel } from "client/Api/Hub/common";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	HTMLElementType,
	ReactElement,
	useCallback
} from "react";
import { HubItemStatistic } from "../common";
import styles from "./CardBase.module.less";
import { setTrackingId } from "client/Misc/utils";
import classNames from "classnames";
import { TimeZone } from "client/DateAndTime/timezones";
import { localize } from "client_legacy/ndaw/culture";
import { CardStatus } from "./CardStatus/CardStatus";
import { CardBaseInfo } from "./CardBaseInfo";

export type CardBaseProps = {
	itemKind: HubItemKind;
	id: number;
	title: string;
	media: HubItemMediaPreviewViewModel;
	startDate: string | Date;
	statistics: readonly HubItemStatistic[];
	categoryNames: readonly string[];
	onOpen: (id: number) => void;
	size?: "large" | "small";
	shouldHideSecondaryInfo?: boolean;
	isPinned?: boolean;
	hasTopBarBackdrop?: boolean;
	timezone: TimeZone;
	isNew: boolean;
};

export const CardBase = (props: CardBaseProps): ReactElement => {
	const {
		itemKind,
		id,
		title,
		media,
		startDate,
		statistics,
		categoryNames,
		size = "default",
		shouldHideSecondaryInfo,
		onOpen,
		isPinned,
		hasTopBarBackdrop = false,
		timezone,
		isNew
	} = props;

	const clickHandler = useCallback(
		(): void => {
			onOpen(id);
		},
		[
			id,
			onOpen
		]
	);

	const isSmall: boolean = size === "small";
	const TitleTag: HTMLElementType = isSmall
		? "h4"
		: "h3";

	return (
		<div
			{...setTrackingId(`hub-card_${itemKind}|${id}`)}
			className={classNames(
				styles.root,
				{
					[styles.root_small]: isSmall,
					[styles.root_hasTopBarBackdrop]: hasTopBarBackdrop,
				}
			)}
			onClick={clickHandler}
		>
			<div className={styles.media}>
				<HubThumbnail
					hubItemKind={itemKind}
					media={media}
					title={title}
					mediaPlaceholderSize="large"
				/>
			</div>
			<div className={styles.content}>
				{isNew && (
					<div className={styles.new}>
						<CardStatus
							trackingId="status"
						>
							{localize("_New")}
						</CardStatus>
					</div>
				)}

				<TitleTag
					{...setTrackingId("title")}
					className={styles.title}
				>
					{isPinned && (
						<span
							{...setTrackingId("pinned")}
							className={styles.titleIcon}
						>
							<Icon value="thumbtack" />
						</span>
					)}
					{title}
				</TitleTag>
				<CardBaseInfo
					startDate={startDate}
					dateFormat={
						itemKind === HubItemKind.Event
							? "RelativeDayWithTime"
							: "RelativeTimestamp"
					}
					timezone={timezone}
					statistics={statistics}
					shouldHideSecondaryInfo={shouldHideSecondaryInfo}
					categoryNames={categoryNames}
				/>
			</div>
		</div>
	);
};
CardBase.displayName = "CardBase";
